/**
 * WalletWidget Component
 * Displays student's rewards wallet with unclaimed, claimed, and used items
 */

import React, { useState, useEffect } from 'react';
import { getWalletItems, claimReward } from '../../walletService';
import { FaGift, FaTicketAlt, FaLightbulb, FaClock, FaCheck } from 'react-icons/fa';

export default function WalletWidget({ studentId, isArabic }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Separate items by status
  const unclaimedItems = items.filter(i => i.status === 'unclaimed');
  const claimedCoupons = items.filter(i => i.status === 'claimed');
  const usedItems = items.filter(i => i.status === 'used' || i.status === 'expired');

  useEffect(() => {
    loadWalletItems();
  }, [studentId]);

  async function loadWalletItems() {
    setLoading(true);
    const { data, error } = await getWalletItems('unclaimed,claimed,used,expired');

    if (error) {
      console.error('Failed to load wallet items:', error);
      setErrorMessage(error);
    } else {
      setItems(data || []);
    }

    setLoading(false);
  }

  async function handleClaim(itemId) {
    setClaiming(itemId);
    setSuccessMessage('');
    setErrorMessage('');

    const { data, error } = await claimReward(itemId);

    if (error) {
      setErrorMessage(error);
    } else {
      setSuccessMessage(isArabic ? 'تم المطالبة بالمكافأة بنجاح!' : 'Reward claimed successfully!');
      // Reload wallet to show updated status
      await loadWalletItems();
    }

    setClaiming(null);
  }

  function copyCouponCode(code) {
    navigator.clipboard.writeText(code);
    setSuccessMessage(isArabic ? 'تم نسخ كود الكوبون!' : 'Coupon code copied!');
    setTimeout(() => setSuccessMessage(''), 2000);
  }

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>{isArabic ? 'جاري التحميل...' : 'Loading wallet...'}</p>
      </div>
    );
  }

  return (
    <div style={styles.container} dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaGift style={{ fontSize: 24, color: '#FF7A1A' }} />
          <h2 style={styles.title}>
            {isArabic ? 'محفظة المكافآت' : 'Rewards Wallet'}
          </h2>
        </div>
        {unclaimedItems.length > 0 && (
          <span style={styles.badge}>{unclaimedItems.length}</span>
        )}
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div style={styles.successMessage}>{successMessage}</div>
      )}
      {errorMessage && (
        <div style={styles.errorMessage}>{errorMessage}</div>
      )}

      {/* Unclaimed Rewards Section */}
      {unclaimedItems.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {isArabic ? 'مكافآت جديدة - اضغط للمطالبة' : 'New Rewards - Click to Claim'}
          </h3>
          <div style={styles.grid}>
            {unclaimedItems.map(item => (
              <div key={item.id} style={styles.rewardCard}>
                <div style={styles.rewardIcon}>
                  <FaGift style={{ fontSize: 32, color: '#FF7A1A' }} />
                </div>
                <div style={styles.rewardDetails}>
                  <div style={styles.levelBadge}>
                    {isArabic ? `المستوى ${item.level_earned}` : `Level ${item.level_earned}`}
                  </div>
                  <div style={styles.rewardLabel}>{item.reward_label}</div>
                </div>
                <button
                  onClick={() => handleClaim(item.id)}
                  disabled={claiming === item.id}
                  style={claiming === item.id ? styles.buttonDisabled : styles.claimButton}
                >
                  {claiming === item.id
                    ? (isArabic ? 'جاري المطالبة...' : 'Claiming...')
                    : (isArabic ? 'المطالبة' : 'Claim')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Claimed Coupons Section */}
      {claimedCoupons.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {isArabic ? 'كوبوناتي - جاهزة للاستخدام' : 'My Coupons - Ready to Use'}
          </h3>
          <div style={styles.grid}>
            {claimedCoupons.map(coupon => (
              <div key={coupon.id} style={styles.couponCard}>
                <div style={styles.couponIcon}>
                  <FaTicketAlt style={{ fontSize: 32, color: '#FF7A1A' }} />
                </div>
                <div style={styles.couponDetails}>
                  <div style={styles.couponLabel}>{coupon.reward_label}</div>
                  <div style={styles.couponCode}>{coupon.coupon_code}</div>
                  {coupon.expires_at && (
                    <div style={styles.expiryDate}>
                      {isArabic ? 'ينتهي: ' : 'Expires: '}
                      {new Date(coupon.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => copyCouponCode(coupon.coupon_code)}
                  style={styles.copyButton}
                >
                  {isArabic ? 'نسخ' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
          <p style={styles.hint}>
            {isArabic
              ? 'استخدم هذه الكوبونات عند حجز الجلسات للحصول على الخصم'
              : 'Use these coupons when booking sessions to get your discount'}
          </p>
        </div>
      )}

      {/* Used/Expired Items Section */}
      {usedItems.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {isArabic ? 'السجل' : 'History'}
          </h3>
          <div style={styles.grid}>
            {usedItems.map(item => (
              <div key={item.id} style={styles.usedCard}>
                <div style={styles.usedIcon}>
                  {item.status === 'used' ? <FaCheck style={{ fontSize: 20, color: '#10B981' }} /> : <FaClock style={{ fontSize: 20, color: '#F59E0B' }} />}
                </div>
                <div style={styles.usedDetails}>
                  <div style={styles.usedLabel}>{item.reward_label}</div>
                  <div style={styles.usedStatus}>
                    {item.status === 'used'
                      ? (isArabic ? 'مستخدم' : 'Used')
                      : (isArabic ? 'منتهي الصلاحية' : 'Expired')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FaGift style={{ fontSize: 64, color: '#94A3B8' }} />
          </div>
          <p style={styles.emptyText}>
            {isArabic
              ? 'لا توجد مكافآت بعد. استمر في كسب XP للوصول إلى مستويات جديدة والحصول على مكافآت!'
              : 'No rewards yet. Keep earning XP to reach new levels and unlock rewards!'}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = {
  container: {
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    padding: '24px',
    marginTop: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  },
  badge: {
    backgroundColor: '#ef4444',
    color: 'white',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '48px',
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  successMessage: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  errorMessage: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  rewardCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    border: '2px solid #10b981',
  },
  rewardIcon: {
    fontSize: '32px',
  },
  rewardDetails: {
    flex: 1,
  },
  levelBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: '4px',
  },
  rewardLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937',
  },
  claimButton: {
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonDisabled: {
    backgroundColor: '#9ca3af',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'not-allowed',
  },
  couponCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    border: '2px solid #3b82f6',
  },
  couponIcon: {
    fontSize: '32px',
  },
  couponDetails: {
    flex: 1,
  },
  couponLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px',
  },
  couponCode: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#3b82f6',
    backgroundColor: '#eff6ff',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: '4px',
  },
  expiryDate: {
    fontSize: '12px',
    color: '#6b7280',
  },
  copyButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  hint: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '12px',
    fontStyle: 'italic',
  },
  usedCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    border: '1px solid #e5e7eb',
    opacity: 0.7,
  },
  usedIcon: {
    fontSize: '24px',
    color: '#9ca3af',
  },
  usedDetails: {
    flex: 1,
  },
  usedLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
  },
  usedStatus: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b7280',
    maxWidth: '400px',
    margin: '0 auto',
  },
};
